import { logUsage } from 'owa-analytics';
import { API_BASE_FOR_SHARES } from 'owa-fileprovider-services';
import { lazyUpdateRecipientPermissionInfo } from 'owa-link-data';
import { GetPermissionInfoStatus, RecipientPermissionInfo } from 'owa-sharing-data';
import { format } from 'owa-strings';
import { trace } from 'owa-trace';
import { parse as parseUrl } from 'url';

function createCORSRequest(method, url): XMLHttpRequest {
    let xhr = new XMLHttpRequest();
    if ('withCredentials' in xhr) {
        // Most browsers.
        xhr.open(method, url, true);
    } else {
        xhr = null;
        trace.warn('CORS not supported');
    }
    return xhr;
}

interface RecipientInfo {
    alias: string;
}
interface RecipientPermissionResult {
    canRead: boolean;
    canReview: boolean;
    canWrite: boolean;
    hasAccess: boolean;
    canBeInvited: boolean;
    recipient: RecipientInfo;
}
export function checkRecipientPermissionService(
    linkId: string,
    location: string,
    token: string,
    recipients: RecipientPermissionInfo[]
) {
    try {
        const requestFormat = 'https://{0}/{1}/{2}/permission/checkAccess';
        const url = format(
            requestFormat,
            parseUrl(location).hostname,
            API_BASE_FOR_SHARES,
            'u!' + getBase64EncodedUrlForVroom(location)
        );
        const method = 'POST';
        const xhr = createCORSRequest(method, url);
        if (!xhr) {
            return;
        }

        xhr.onload = async function() {
            try {
                const recipientPermissionResults: RecipientPermissionResult[] = JSON.parse(
                    xhr.responseText
                ).value;

                let atLeastOneRecipientCanNotAccess: boolean = false;
                const updateRecipientPermissionInfo = await lazyUpdateRecipientPermissionInfo.import();

                recipientPermissionResults.forEach(recipientPermission => {
                    updateRecipientPermissionInfo(
                        linkId,
                        recipientPermission.recipient.alias,
                        recipientPermission.hasAccess,
                        recipientPermission.canBeInvited,
                        GetPermissionInfoStatus.Succeeded
                    );
                    atLeastOneRecipientCanNotAccess =
                        atLeastOneRecipientCanNotAccess || !recipientPermission.hasAccess;
                });
                logDatapoint(atLeastOneRecipientCanNotAccess, recipients.length);
                trace.info(
                    format(
                        'RecipientPermission: checkAccess for {0} recipients succeeded',
                        recipients.length
                    )
                );
            } catch (error) {
                handleFailure(linkId, recipients, 'Parse CheckAccess Response: ' + error.Message);
            }
        };

        xhr.onerror = function() {
            // Error code goes here.
            handleFailure(linkId, recipients, xhr.responseText);
        };

        xhr.ontimeout = function() {
            handleFailure(linkId, recipients, 'Request timed out');
        };

        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json;odata=verbose');
        xhr.setRequestHeader('Authorization', token);
        const body = JSON.stringify(createRequestBody(recipients));
        xhr.send(body);
    } catch (error) {
        handleFailure(linkId, recipients, error.Message);
    }
}

function getBase64EncodedUrlForVroom(location: string): string {
    const base64Value = btoa(location);

    return base64Value
        .replace(/=+$/, '')
        .replace('/', '_')
        .replace('+', '-');
}

function createRequestBody(recipientPermissionInfos: RecipientPermissionInfo[]) {
    return {
        recipients: recipientPermissionInfos.map(function(info) {
            return { alias: info.emailAddress };
        }),
    };
}

async function handleFailure(
    linkId: string,
    recipientPermissionInfos: RecipientPermissionInfo[],
    errorMessage: string
) {
    trace.info(
        format(
            'RecipientPermission: checkAccess for {0} recipients failed',
            recipientPermissionInfos.length
        )
    );

    const updateRecipientPermissionInfo = await lazyUpdateRecipientPermissionInfo.import();

    recipientPermissionInfos.forEach(recipientPermissionInfo => {
        updateRecipientPermissionInfo(
            linkId,
            recipientPermissionInfo.emailAddress,
            false, // hasAccess
            false, // canBeInvited
            GetPermissionInfoStatus.Failed
        );
    });

    logUsage('CheckRecipientPermissionFailure');
}

function logDatapoint(atLeastOneRecipientCanNotAccess: boolean, recipientCount: number) {
    logUsage('CheckPermissionForRecipients', [
        atLeastOneRecipientCanNotAccess,
        Math.min(recipientCount, 23),
    ]);
}
